import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import { Helmet } from 'react-helmet';

const STATS = [
  { value: '2,400+', label: 'Active Partners' },
  { value: '98.6%', label: 'Compliance Rate' },
  { value: 'OMR 1.2B', label: 'Partner Revenue' },
];

const FEATURES = [
  { icon: 'ShieldCheck', text: 'End-to-end lifecycle management' },
  { icon: 'BarChart3', text: 'Real-time performance analytics' },
  { icon: 'Zap', text: 'AI-powered partner scoring' },
];

const SignupPage = () => {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [activeOrb, setActiveOrb] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveOrb((prev) => (prev + 1) % 3);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password?.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      const { error: signUpError } = await signUp(email, password, {
        full_name: fullName,
        role: 'viewer',
      });
      if (signUpError) {
        setError(signUpError?.message || 'Failed to create account');
      } else {
        navigate('/');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Create Account — PartnerHub</title>
      </Helmet>
      <div className="min-h-screen flex overflow-hidden" style={{ fontFamily: "'DM Sans', sans-serif" }}>

        {/* ── LEFT PANEL ── */}
        <div className="hidden lg:flex lg:w-[55%] relative flex-col justify-between p-16 overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #0a0f1e 0%, #0d1b3e 40%, #0a1628 70%, #060d1a 100%)' }}>

          <div className="absolute inset-0 pointer-events-none">
            <svg className="absolute inset-0 w-full h-full opacity-[0.04]" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse">
                  <path d="M 60 0 L 0 0 0 60" fill="none" stroke="#4f8ef7" strokeWidth="0.5"/>
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>
            <div className="absolute top-[15%] left-[10%] w-80 h-80 rounded-full opacity-20 transition-all duration-[3000ms]"
              style={{ background: activeOrb === 0 ? 'radial-gradient(circle, #3b82f6 0%, transparent 70%)' : 'radial-gradient(circle, #1d4ed8 0%, transparent 70%)', filter: 'blur(40px)' }} />
            <div className="absolute top-[50%] right-[5%] w-96 h-96 rounded-full opacity-15 transition-all duration-[3000ms]"
              style={{ background: activeOrb === 1 ? 'radial-gradient(circle, #6366f1 0%, transparent 70%)' : 'radial-gradient(circle, #4338ca 0%, transparent 70%)', filter: 'blur(50px)' }} />
            <div className="absolute bottom-[10%] left-[20%] w-72 h-72 rounded-full opacity-20 transition-all duration-[3000ms]"
              style={{ background: activeOrb === 2 ? 'radial-gradient(circle, #0ea5e9 0%, transparent 70%)' : 'radial-gradient(circle, #0284c7 0%, transparent 70%)', filter: 'blur(35px)' }} />
            {[...Array(12)]?.map((_, i) => (
              <div key={i}
                className="absolute w-1 h-1 rounded-full bg-blue-400/30"
                style={{
                  left: `${10 + (i * 7.5) % 80}%`,
                  top: `${15 + (i * 13) % 70}%`,
                  animation: `float ${3 + (i % 4)}s ease-in-out infinite alternate`,
                  animationDelay: `${i * 0.4}s`,
                }}
              />
            ))}
          </div>

          <div className="relative z-10 flex items-center gap-12">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #3b82f6, #6366f1)' }}>
              <Icon name="Handshake" size={20} className="text-white" />
            </div>
            <span className="text-white font-bold text-lg tracking-tight">PartnerHub</span>
            <span className="ml-8 px-8 py-4 rounded-full text-[10px] font-semibold tracking-widest uppercase"
              style={{ background: 'rgba(59,130,246,0.15)', color: '#60a5fa', border: '1px solid rgba(59,130,246,0.25)' }}>
              Enterprise
            </span>
          </div>

          <div className="relative z-10 flex-1 flex flex-col justify-center py-16">
            <div className="flex items-center gap-8 mb-24">
              <div className="w-8 h-px" style={{ background: 'linear-gradient(90deg, #3b82f6, transparent)' }} />
              <span className="text-xs font-semibold tracking-[0.2em] uppercase" style={{ color: '#60a5fa' }}>
                Partner Lifecycle Platform
              </span>
            </div>
            <h1 className="text-5xl font-bold leading-[1.1] mb-24 text-white" style={{ letterSpacing: '-0.02em' }}>
              Join the future of
              <br />
              <span style={{ background: 'linear-gradient(90deg, #60a5fa, #a78bfa, #38bdf8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                partner management
              </span>
            </h1>
            <p className="text-base leading-relaxed mb-48 max-w-sm" style={{ color: '#94a3b8' }}>
              Create your account and start managing partner relationships with precision, compliance, and AI-driven insights.
            </p>
            <div className="space-y-16 mb-48">
              {FEATURES?.map((f, i) => (
                <div key={i} className="flex items-center gap-16">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.2)' }}>
                    <Icon name={f?.icon} size={14} className="text-blue-400" />
                  </div>
                  <span className="text-sm" style={{ color: '#cbd5e1' }}>{f?.text}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-0">
              {STATS?.map((s, i) => (
                <React.Fragment key={i}>
                  <div className="flex-1">
                    <div className="text-2xl font-bold text-white" style={{ letterSpacing: '-0.02em' }}>{s?.value}</div>
                    <div className="text-xs mt-4" style={{ color: '#64748b' }}>{s?.label}</div>
                  </div>
                  {i < STATS?.length - 1 && (
                    <div className="w-px h-10 mx-16" style={{ background: 'rgba(255,255,255,0.08)' }} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="relative z-10 rounded-2xl p-24"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(12px)' }}>
            <div className="flex items-start gap-16">
              <div className="w-10 h-10 rounded-full shrink-0 flex items-center justify-center text-sm font-bold text-white"
                style={{ background: 'linear-gradient(135deg, #3b82f6, #6366f1)' }}>
                SR
              </div>
              <div>
                <p className="text-sm leading-relaxed mb-12" style={{ color: '#94a3b8' }}>
                  "PartnerHub reduced our partner onboarding time by 60% and gave us complete visibility into compliance across 400+ partners."
                </p>
                <div className="flex items-center gap-8">
                  <span className="text-xs font-semibold text-white">Sarah Reynolds</span>
                  <span className="text-xs" style={{ color: '#475569' }}>· VP Partnerships, Nexora Inc.</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── RIGHT PANEL ── */}
        <div className="flex-1 flex flex-col justify-center items-center p-8 lg:p-16 relative overflow-hidden"
          style={{ background: '#f8fafc' }}>

          <div className="absolute inset-0 pointer-events-none opacity-40">
            <div className="absolute top-0 right-0 w-96 h-96 rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.06) 0%, transparent 70%)', transform: 'translate(30%, -30%)' }} />
            <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.06) 0%, transparent 70%)', transform: 'translate(-30%, 30%)' }} />
          </div>

          <div className="relative w-full max-w-[420px]">

            <div className="flex lg:hidden items-center justify-center gap-12 mb-48">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #3b82f6, #6366f1)' }}>
                <Icon name="Handshake" size={20} className="text-white" />
              </div>
              <span className="font-bold text-xl text-slate-900">PartnerHub</span>
            </div>

            <div className="mb-40">
              <h2 className="text-3xl font-bold text-slate-900 mb-8" style={{ letterSpacing: '-0.02em' }}>
                Create your account
              </h2>
              <p className="text-sm" style={{ color: '#64748b' }}>
                Join PartnerHub to manage your partner ecosystem
              </p>
            </div>

            {error && (
              <div className="flex items-center gap-12 p-16 rounded-xl mb-28 text-sm"
                style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626' }}>
                <Icon name="AlertCircle" size={16} className="shrink-0" />
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-20">
              {/* Full Name */}
              <div>
                <label className="block text-sm font-semibold mb-8" style={{ color: '#374151' }}>
                  Full name
                </label>
                <div className="relative">
                  <div className="absolute left-16 top-1/2 -translate-y-1/2" style={{ color: '#9ca3af' }}>
                    <Icon name="User" size={16} />
                  </div>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e?.target?.value)}
                    required
                    placeholder="John Smith"
                    className="w-full pl-48 pr-16 text-sm transition-all duration-200 outline-none"
                    style={{
                      height: '48px',
                      background: '#ffffff',
                      border: '1.5px solid #e2e8f0',
                      borderRadius: '12px',
                      color: '#0f172a',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    }}
                    onFocus={(e) => { e.target.style.border = '1.5px solid #3b82f6'; e.target.style.boxShadow = '0 0 0 4px rgba(59,130,246,0.08)'; }}
                    onBlur={(e) => { e.target.style.border = '1.5px solid #e2e8f0'; e.target.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; }}
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-semibold mb-8" style={{ color: '#374151' }}>
                  Email address
                </label>
                <div className="relative">
                  <div className="absolute left-16 top-1/2 -translate-y-1/2" style={{ color: '#9ca3af' }}>
                    <Icon name="Mail" size={16} />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e?.target?.value)}
                    required
                    placeholder="you@company.com"
                    className="w-full pl-48 pr-16 text-sm transition-all duration-200 outline-none"
                    style={{
                      height: '48px',
                      background: '#ffffff',
                      border: '1.5px solid #e2e8f0',
                      borderRadius: '12px',
                      color: '#0f172a',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    }}
                    onFocus={(e) => { e.target.style.border = '1.5px solid #3b82f6'; e.target.style.boxShadow = '0 0 0 4px rgba(59,130,246,0.08)'; }}
                    onBlur={(e) => { e.target.style.border = '1.5px solid #e2e8f0'; e.target.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; }}
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-semibold mb-8" style={{ color: '#374151' }}>
                  Password
                </label>
                <div className="relative">
                  <div className="absolute left-16 top-1/2 -translate-y-1/2" style={{ color: '#9ca3af' }}>
                    <Icon name="Lock" size={16} />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e?.target?.value)}
                    required
                    placeholder="Min. 8 characters"
                    className="w-full pl-48 pr-48 text-sm transition-all duration-200 outline-none"
                    style={{
                      height: '48px',
                      background: '#ffffff',
                      border: '1.5px solid #e2e8f0',
                      borderRadius: '12px',
                      color: '#0f172a',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    }}
                    onFocus={(e) => { e.target.style.border = '1.5px solid #3b82f6'; e.target.style.boxShadow = '0 0 0 4px rgba(59,130,246,0.08)'; }}
                    onBlur={(e) => { e.target.style.border = '1.5px solid #e2e8f0'; e.target.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-16 top-1/2 -translate-y-1/2 transition-colors"
                    style={{ color: '#9ca3af' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#64748b'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}>
                    <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} />
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-semibold mb-8" style={{ color: '#374151' }}>
                  Confirm password
                </label>
                <div className="relative">
                  <div className="absolute left-16 top-1/2 -translate-y-1/2" style={{ color: '#9ca3af' }}>
                    <Icon name="Lock" size={16} />
                  </div>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e?.target?.value)}
                    required
                    placeholder="Repeat your password"
                    className="w-full pl-48 pr-48 text-sm transition-all duration-200 outline-none"
                    style={{
                      height: '48px',
                      background: '#ffffff',
                      border: '1.5px solid #e2e8f0',
                      borderRadius: '12px',
                      color: '#0f172a',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    }}
                    onFocus={(e) => { e.target.style.border = '1.5px solid #3b82f6'; e.target.style.boxShadow = '0 0 0 4px rgba(59,130,246,0.08)'; }}
                    onBlur={(e) => { e.target.style.border = '1.5px solid #e2e8f0'; e.target.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-16 top-1/2 -translate-y-1/2 transition-colors"
                    style={{ color: '#9ca3af' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#64748b'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}>
                    <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={16} />
                  </button>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-10 text-sm font-semibold text-white transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  height: '50px',
                  borderRadius: '12px',
                  background: loading ? '#3b82f6' : 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 50%, #6366f1 100%)',
                  boxShadow: loading ? 'none' : '0 4px 20px rgba(59,130,246,0.35), 0 1px 3px rgba(0,0,0,0.1)',
                }}
                onMouseEnter={(e) => { if (!loading) e.currentTarget.style.transform = 'translateY(-1px)'; if (!loading) e.currentTarget.style.boxShadow = '0 8px 28px rgba(59,130,246,0.45), 0 2px 6px rgba(0,0,0,0.12)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = loading ? 'none' : '0 4px 20px rgba(59,130,246,0.35), 0 1px 3px rgba(0,0,0,0.1)'; }}>
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 rounded-full animate-spin"
                      style={{ borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white' }} />
                    Creating account...
                  </>
                ) : (
                  <>
                    <Icon name="UserPlus" size={16} />
                    Create account
                  </>
                )}
              </button>
            </form>

            <div className="flex items-center gap-16 my-28">
              <div className="flex-1 h-px" style={{ background: '#e2e8f0' }} />
              <span className="text-xs font-medium" style={{ color: '#94a3b8' }}>Already have an account?</span>
              <div className="flex-1 h-px" style={{ background: '#e2e8f0' }} />
            </div>

            <Link
              to="/login"
              className="w-full flex items-center justify-center gap-10 text-sm font-semibold transition-all duration-200"
              style={{
                height: '48px',
                borderRadius: '12px',
                background: '#ffffff',
                border: '1.5px solid #e2e8f0',
                color: '#374151',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                textDecoration: 'none',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.border = '1.5px solid #bfdbfe'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(59,130,246,0.1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.border = '1.5px solid #e2e8f0'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; }}>
              <Icon name="LogIn" size={16} />
              Sign in instead
            </Link>

            <p className="text-center text-xs mt-28" style={{ color: '#94a3b8' }}>
              Protected by enterprise-grade security &amp; SOC 2 compliance
            </p>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes float {
          0% { transform: translateY(0px) scale(1); opacity: 0.3; }
          100% { transform: translateY(-12px) scale(1.1); opacity: 0.6; }
        }
        input::placeholder { color: #9ca3af; }
      `}</style>
    </>
  );
};

export default SignupPage;
